import Alert from "@/Components/Alert/Alert";
import { useSetModal } from "@/shared/atoms/useModal";
import usePosOrderDeliveryMethod from "@/shared/atoms/usePosOrderDeliveryMethodAtom";
import usePosOrderPayment from "@/shared/atoms/usePosOrderPaymentAtom";
import usePosOrderUser from "@/shared/atoms/usePosOrderUser";
import { useCart } from "@/shared/hooks/useCartHook";
import useCartValidatorHook from "@/shared/hooks/useCartValidatorHook";
import useIsOrderEditingHook from "@/shared/hooks/useIsOrderEditingHook";
import useIsOrderUserValidHook from "@/shared/hooks/useIsOrderUserValidHook";
import {
  useHasOrders,
  useHasSession,
  useIsTableAndNoSession,
  useIsTableMethod,
  useSessionTotalAmount,
  useShowPayments,
} from "@/shared/hooks/usePosOrderTableSessionHook";
import usePosOrderTotalAmountHook from "@/shared/hooks/usePosOrderTotalAmountHook";
import usePosSessionPay from "@/shared/mutations/pos/usePosSessionPay";
import { PaymentMethodEnum } from "@/shared/utils/enum";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PosCartOrderButton from "./PosCartOrderButton";
import PosCartPriceBreakdown from "./PosCartPriceBreakdown";
import usePosOrderHook from "@/shared/hooks/usePosOrderHook";
import PosCartPaymentMethod from "./PosCartPaymentMethod";
import usePaymentHook from "@/shared/hooks/usePaymentHook";

const PosCartBottom2 = () => {
  const { t } = useTranslation();
  const { isPending: cartValidationPending } = useCartValidatorHook();
  const [deliveryMethod] = usePosOrderDeliveryMethod();
  const showPayments = useShowPayments();
  const hasOrders = useHasOrders();
  const sessionTotalAmount = useSessionTotalAmount();
  const isTableAndNoSession = useIsTableAndNoSession();
  const { isPending: isPayingSession } = usePosSessionPay();
  const [error, setError] = useState<string>("");
  const [orderUser] = usePosOrderUser();
  const { isOrderUserValid } = useIsOrderUserValidHook({
    selectedDelivery: deliveryMethod,
    orderUser,
  });
  const [paymentId] = usePosOrderPayment();
  const { isOrderEditing, editOrder } = useIsOrderEditingHook();
  const totalAmount = usePosOrderTotalAmountHook();
  const setModal = useSetModal();
  const hasSession = useHasSession();
  const [cart] = useCart();
  const isTableMethod = useIsTableMethod();
  const isEditingPlacedOrder = isOrderEditing && editOrder?.isEditing !== false;
  const { isOnlinePayment } = usePaymentHook();
  const initialPayment = useRef(
    editOrder?.order_information?.payment_method_id
  );

  const { handlePostOrder, isPending } = usePosOrderHook();

  const isTotalAmountDisabled = [
    PaymentMethodEnum.CASH,
    PaymentMethodEnum.SPLIT_PAYMENT,
    PaymentMethodEnum.PAYMENT_METHOD_NOT_KNOWN_YET,
    PaymentMethodEnum.INVOICE_LATER,
  ].includes(paymentId)
    ? false
    : hasSession
      ? showPayments && sessionTotalAmount === 0
      : totalAmount === 0;

  const handleOpenPaymentModal = (isSession: boolean) => {
    setError("");
    setModal("posCartPayment", { isSession });
  };

  const handleChangePaymentMethod = () => {
    setError("");
    setModal("posCartPayment", {
      isSession: false,
      isChangePaymentMethod: true,
    });
  };

  return (
    <div className="bg-white p-4 border border-border rounded-lg">
      <PosCartPriceBreakdown isCartValidating={cartValidationPending} />

      {error && (
        <div className="mb-3">
          <Alert onClickClose={() => setError("")}>{error}</Alert>
        </div>
      )}

      {hasOrders && !isOrderEditing && showPayments ? (
        <PosCartOrderButton
          isPending={isPayingSession}
          isDisabled={isTotalAmountDisabled}
          onClick={() => {
            if (isTotalAmountDisabled) {
              setError(t("posOrders.totalAmountIsZero"));
              return;
            }
            handleOpenPaymentModal(true);
          }}
        >
          {t("payOrder")}
        </PosCartOrderButton>
      ) : (
        <>
          {isEditingPlacedOrder && !isTableMethod && (
            <PosCartPaymentMethod
              paymentId={paymentId}
              isDisabled={
                cart.length === 0 ||
                !isOrderUserValid ||
                isPending ||
                isOnlinePayment(initialPayment.current)
              }
              onChange={handleChangePaymentMethod}
            />
          )}
          <PosCartOrderButton
            isPending={isPending || cartValidationPending}
            isDisabled={
              cart.length === 0 ||
              !isOrderUserValid ||
              isTotalAmountDisabled ||
              isTableAndNoSession
            }
            onClick={() => {
              if (cart.length === 0) {
                setError(t("posOrders.addProduct"));
                return;
              } else if (isTotalAmountDisabled) {
                setError(t("posOrders.totalAmountIsZero"));
                return;
              } else if (!isOrderUserValid) {
                setError(t("posOrders.enterTheUserInfo"));
                return;
              } else if (isTableAndNoSession) {
                setError(t("posOrders.dineInAndNoSession"));
                return;
              } else if (
                isEditingPlacedOrder ||
                (isTableMethod &&
                  paymentId === PaymentMethodEnum.PAYMENT_METHOD_NOT_KNOWN_YET)
              ) {
                handlePostOrder();
                return;
              }
              handleOpenPaymentModal(false);
            }}
          >
            {t("posOrders.order")}
          </PosCartOrderButton>
        </>
      )}
    </div>
  );
};

export default PosCartBottom2;
